import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsAccessibility from "highcharts/modules/accessibility";
import { useTheme } from "@mui/material/styles";
import { useGetPayrollStaffDhashboardQuery } from "../../../../Api/attoDeskApi";
import { IEmployeePayroll } from "../../../../Api/Interface/api.interface";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";

HighchartsMore(Highcharts);
HighchartsExporting(Highcharts);
HighchartsAccessibility(Highcharts);

interface AttendanceEntry {
  date: string;
  schedules: { start: Date; end: Date }[];
  actualPunches?: { start: Date; end: Date }[];
}

interface PayrollDashboardProps {
  staffId: string;
  selectedStartDate: Dayjs;
  selectedEndDate: Dayjs;
}

const PayrollDashboard: React.FC<PayrollDashboardProps> = ({
  staffId,
  selectedStartDate,
  selectedEndDate,
}) => {
  const theme = useTheme();
  const [showTable, setShowTable] = useState(false);

  // ✅ Call the API
  const { data: payrollApiData, isLoading } = useGetPayrollStaffDhashboardQuery(
    {
      empid: staffId,
      startDate: selectedStartDate.format("YYYY-MM-DD"),
      endDate: selectedEndDate.format("YYYY-MM-DD"),
    }
  );

  const payrollData = useMemo(() => {
    return (payrollApiData?.data as IEmployeePayroll[]) || [];
  }, [payrollApiData?.data]);

  // convert strings to numbers
  const categories = payrollData.map((d) => d.payrollGenerateId);
  const basicSalaries = payrollData.map((d) => Number(d.gross_salary || 0));
  const overtimeSalaries = payrollData.map((d) => Number(d.ot_salary || 0));
  const bonusSalaries = payrollData.map((d) => Number(d.bonus_amount || 0));
  const totalSalaries = payrollData.map(
    (d) =>
      Number(d.gross_salary || 0) +
      Number(d.ot_salary || 0) +
      Number(d.bonus_amount || 0)
  );

  if (isLoading) return <p>Loading payroll data...</p>;

  const salaryTrendOptions: Highcharts.Options = {
    chart: { type: "column", backgroundColor: theme.palette.cardAdd.bgColor },
    title: {
      text: "Payroll Overview",
      style: {
        fontSize: "0.9375rem",
        color: theme.palette.heading.headingColor,
      },
    },
    xAxis: {
      categories,
      crosshair: true,
      title: {
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
      labels: {
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: "Amount ($)",
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
      labels: {
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
    },
    tooltip: { shared: true, valueSuffix: " $" },
    plotOptions: {
      column: { stacking: "normal", borderRadius: 5 },
      area: { marker: { enabled: true, radius: 4 } },
    },
    legend: {
      itemStyle: {
        fontSize: "0.75rem",
        color: theme.palette.heading.headingColor,
      },
    },
    series: [
      {
        type: "column",
        name: "Basic Salary",
        data: basicSalaries,
        color: Highcharts.color(theme.palette.primary.main).get(),
      },
      {
        type: "column",
        name: "Overtime",
        data: overtimeSalaries,
        color:  Highcharts.color(theme.palette.primary.main).brighten(-0.1).get(),
      },
      {
        type: "column",
        name: "Bonus",
        data: bonusSalaries,
        color:  Highcharts.color(theme.palette.primary.main).brighten(0.2).get(),
      },
      {
        type: "area",
        name: "Total",
        data: totalSalaries,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [[1, "transparent"]],
        },
        lineWidth: 2,
        color: "#434348",
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div style={{ padding: "5px" }}>
      {payrollData.length > 0 ? (
        <>
          <HighchartsReact
            highcharts={Highcharts}
            options={salaryTrendOptions}
          />
          <Typography
            onClick={() => setShowTable(!showTable)}
            sx={{ cursor: "pointer", textAlign: "center", mt: 1 }}
            variant="h6"
          >
            {showTable ? "Hide Table View" : "View Table"}
          </Typography>
        </>
      ) : (
        <>
          <HighchartsReact
            highcharts={Highcharts}
            options={salaryTrendOptions}
          />
        </>
      )}
      {showTable && (
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Payroll</strong>
                </TableCell>

                <TableCell>
                  <strong>Normal Working Amount</strong>
                </TableCell>

                <TableCell>
                  <strong>OT Amount</strong>
                </TableCell>

                <TableCell>
                  <strong>Adjustment Amount</strong>
                </TableCell>

                <TableCell>
                  <strong>Total Amount</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {payrollData.map((row: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Typography>
                      {`${row.payrollGenerateId} | ${dayjs(
                        row.startDate
                      ).format("MMM-DD, YYYY")} - ${dayjs(row.endDate).format(
                        "MMM-DD, YYYY"
                      )}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {"$ "}
                      {Number(row?.gross_salary || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {"$ "}
                      {Number(row?.ot_salary || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {"$ "}
                      {Number(row?.bonus_amount || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {"$ "}
                      {Number(row?.total_salary || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default PayrollDashboard;
